import { useState, useRef } from 'react';
import { X, TrendingUp, Upload, Download, CheckCircle, AlertCircle, BarChart3, Clock, Database } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import ModelComparisonModal from './ModelComparisonModal';
import DogLoader from '../ui/DogLoader';
import { forecastsService, CsvType } from '../../services/forecasts.service';

interface CreateForecastModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateForecast: (type: 'HISTORICAL' | 'CSV' | 'HISTORICAL_CSV', data?: any) => void;
  isLoading?: boolean;
}

type ForecastMethod = 'HISTORICAL' | 'CSV';

export default function CreateForecastModal({
  isOpen,
  onClose,
  onCreateForecast,
  isLoading = false,
}: CreateForecastModalProps) {
  const [selectedMethod, setSelectedMethod] = useState<ForecastMethod>('HISTORICAL');
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [csvPreview, setCsvPreview] = useState<string[][] | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // CSV type detection state
  const [detectedCsvType, setDetectedCsvType] = useState<CsvType | null>(null);
  const [detectedChannels, setDetectedChannels] = useState<string[]>([]);
  const [selectedIntervalMinutes, setSelectedIntervalMinutes] = useState<number>(15);

  // New state for historical options
  const [historicalWeeks, setHistoricalWeeks] = useState(12);
  const [forecastWeeks, setForecastWeeks] = useState(8);
  const [selectedModelType, setSelectedModelType] = useState<string>('HISTORICAL_AVERAGE');
  const [showModelComparison, setShowModelComparison] = useState(false);

  // Handle CSV file selection
  const handleFileSelect = (file: File) => {
    setCsvFile(file);
    setValidationError(null);
    setDetectedCsvType(null);
    setDetectedChannels([]);

    // Read and validate CSV
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      // Filter out comment lines (starting with #) before parsing
      const lines = text.split('\n').filter(line => {
        const trimmed = line.trim();
        return trimmed && !trimmed.startsWith('#');
      });
      const rows = lines.map(row => row.split(',').map(cell => cell.trim()));

      // Validate format
      if (rows.length < 2) {
        setValidationError('CSV must have at least a header row and one data row');
        return;
      }

      const headers = rows[0];

      // Use the service's detection logic
      const detection = forecastsService.detectCsvType(headers);
      setDetectedCsvType(detection.type);

      if (detection.type === 'HISTORICAL_ACTUALS') {
        // Extract unique channels from the data
        if (detection.channelColumn) {
          const channelIdx = headers.findIndex(
            (h) => h.toLowerCase() === detection.channelColumn?.toLowerCase()
          );
          if (channelIdx >= 0) {
            const channels = new Set<string>();
            for (let i = 1; i < rows.length; i++) {
              const channel = rows[i][channelIdx]?.trim().toUpperCase();
              if (channel) channels.add(channel);
            }
            setDetectedChannels(Array.from(channels));
          }
        }
      } else {
        // Validate interval predictions format
        const header = headers.map(h => h.toLowerCase());
        const hasCorrectFormat = header.includes('timestamp') && header.includes('predicted value');
        const hasLegacyFormat = header.includes('timestamp') && header.includes('volume');

        if (!hasCorrectFormat && !hasLegacyFormat) {
          setValidationError('CSV must have "Timestamp" and "Predicted Value" columns, or "Date", "Channel", "Volume" columns for historical data. Download the template for the correct format.');
          return;
        }
      }

      // Show preview (first 5 rows)
      setCsvPreview(rows.slice(0, 6));
    };

    reader.readAsText(file);
  };

  // Handle drag and drop
  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  // Download CSV template
  const downloadTemplate = () => {
    // Template matches the format expected by the backend import endpoint
    const template = `Timestamp,Predicted Value,Upper Bound,Lower Bound,Actual Value,Variance,Data Source,Channel
2026-01-01 09:00:00,150,173,128,,,CSV_IMPORT,PHONE
2026-01-01 09:15:00,165,190,140,,,CSV_IMPORT,PHONE
2026-01-01 09:30:00,180,207,153,,,CSV_IMPORT,PHONE
2026-01-01 09:45:00,175,201,149,,,CSV_IMPORT,PHONE
2026-01-01 10:00:00,190,219,162,,,CSV_IMPORT,PHONE`;

    const blob = new Blob([template], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'forecast_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  // Handle model selection from comparison
  const handleModelSelect = (modelType: string, comparisonResults: any) => {
    setSelectedModelType(modelType);
    setHistoricalWeeks(comparisonResults.historicalWeeks);
    setForecastWeeks(comparisonResults.forecastWeeks);
    setShowModelComparison(false);
  };

  // Handle create - don't close modal here, parent will close on success
  // This allows the loading state (DogLoader) to be shown while generating
  const handleCreate = () => {
    if (selectedMethod === 'HISTORICAL') {
      onCreateForecast('HISTORICAL', {
        historicalWeeks,
        forecastWeeks,
        modelType: selectedModelType,
      });
    } else if (selectedMethod === 'CSV' && csvFile) {
      if (detectedCsvType === 'HISTORICAL_ACTUALS') {
        // Historical daily data - use special handler
        onCreateForecast('HISTORICAL_CSV', {
          file: csvFile,
          intervalMinutes: selectedIntervalMinutes,
          channels: detectedChannels,
        });
      } else {
        // Interval predictions - use regular handler
        onCreateForecast('CSV', csvFile);
      }
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 overflow-y-auto">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="flex min-h-full items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="relative bg-white rounded-xl shadow-2xl max-w-3xl w-full"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="h-10 w-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <TrendingUp className="h-6 w-6 text-purple-600" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Create New Forecast</h2>
                  <p className="text-sm text-gray-500">Choose your forecasting method</p>
                </div>
              </div>
              {!isLoading && (
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              )}
            </div>

            {/* Content */}
            <div className="px-6 py-6">
              {isLoading ? (
                /* Dog-themed Loading State - uses rotating messages from DogLoader */
                <DogLoader />
              ) : (
                <>
              {/* Method Selection */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                {/* Historical Data Option */}
                <button
                  onClick={() => setSelectedMethod('HISTORICAL')}
                  className={`p-6 rounded-lg border-2 transition-all ${
                    selectedMethod === 'HISTORICAL'
                      ? 'border-purple-500 bg-purple-50 shadow-md'
                      : 'border-gray-200 hover:border-purple-300 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex flex-col items-center text-center space-y-3">
                    <div
                      className={`h-12 w-12 rounded-full flex items-center justify-center ${
                        selectedMethod === 'HISTORICAL' ? 'bg-purple-600' : 'bg-gray-200'
                      }`}
                    >
                      <TrendingUp
                        className={`h-6 w-6 ${
                          selectedMethod === 'HISTORICAL' ? 'text-white' : 'text-gray-600'
                        }`}
                      />
                    </div>
                    <div>
                      <h3
                        className={`font-semibold text-lg ${
                          selectedMethod === 'HISTORICAL' ? 'text-purple-900' : 'text-gray-900'
                        }`}
                      >
                        Historical Data
                      </h3>
                      <p className="text-sm text-gray-600 mt-1">
                        Auto-select best model from your historical contact volume
                      </p>
                    </div>
                    {selectedMethod === 'HISTORICAL' && (
                      <div className="flex items-center space-x-1 text-xs font-medium text-purple-700">
                        <CheckCircle className="h-4 w-4" />
                        <span>Recommended</span>
                      </div>
                    )}
                  </div>
                </button>

                {/* CSV Upload Option */}
                <button
                  onClick={() => setSelectedMethod('CSV')}
                  className={`p-6 rounded-lg border-2 transition-all ${
                    selectedMethod === 'CSV'
                      ? 'border-indigo-500 bg-indigo-50 shadow-md'
                      : 'border-gray-200 hover:border-indigo-300 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex flex-col items-center text-center space-y-3">
                    <div
                      className={`h-12 w-12 rounded-full flex items-center justify-center ${
                        selectedMethod === 'CSV' ? 'bg-indigo-600' : 'bg-gray-200'
                      }`}
                    >
                      <Upload
                        className={`h-6 w-6 ${
                          selectedMethod === 'CSV' ? 'text-white' : 'text-gray-600'
                        }`}
                      />
                    </div>
                    <div>
                      <h3
                        className={`font-semibold text-lg ${
                          selectedMethod === 'CSV' ? 'text-indigo-900' : 'text-gray-900'
                        }`}
                      >
                        CSV Upload
                      </h3>
                      <p className="text-sm text-gray-600 mt-1">
                        Upload custom forecast data from external sources
                      </p>
                    </div>
                  </div>
                </button>
              </div>

              {/* Method-specific Content */}
              {selectedMethod === 'HISTORICAL' && (
                <div className="space-y-4">
                  {/* Configuration Options */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Historical Weeks
                      </label>
                      <input
                        type="number"
                        min="4"
                        max="52"
                        value={historicalWeeks}
                        onChange={(e) => setHistoricalWeeks(parseInt(e.target.value))}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Weeks of past data to analyze (4-52)
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Forecast Weeks
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="26"
                        value={forecastWeeks}
                        onChange={(e) => setForecastWeeks(parseInt(e.target.value))}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Weeks to forecast ahead (1-26)
                      </p>
                    </div>
                  </div>

                  {/* Model Comparison Button */}
                  <button
                    onClick={() => setShowModelComparison(true)}
                    className="w-full py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg font-semibold hover:from-blue-700 hover:to-indigo-700 transition-all flex items-center justify-center space-x-2"
                  >
                    <BarChart3 className="h-5 w-5" />
                    <span>Compare Forecast Models</span>
                  </button>

                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <div className="flex items-start space-x-3">
                      <AlertCircle className="h-5 w-5 text-purple-600 mt-0.5" />
                      <div>
                        <h4 className="font-semibold text-purple-900 mb-1">
                          Selected Model: {selectedModelType === 'HISTORICAL_AVERAGE' ? 'Historical Average' : selectedModelType}
                        </h4>
                        <p className="text-sm text-purple-700">
                          Click "Compare Forecast Models" to test multiple models and select the most accurate one based on MAPE.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {selectedMethod === 'CSV' && (
                <div className="space-y-4">
                  {/* Download Template */}
                  <div className="flex items-center justify-between bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                    <div className="flex items-center space-x-3">
                      <Download className="h-5 w-5 text-indigo-600" />
                      <div>
                        <div className="text-sm font-medium text-indigo-900">
                          Need a template?
                        </div>
                        <div className="text-xs text-indigo-700">
                          Download our CSV template to get started
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={downloadTemplate}
                      className="px-4 py-2 text-sm font-medium text-indigo-700 bg-white border border-indigo-300 rounded-md hover:bg-indigo-100 transition-colors"
                    >
                      Download Template
                    </button>
                  </div>

                  {/* File Upload Area */}
                  <div
                    className={`relative border-2 border-dashed rounded-lg p-8 transition-colors ${
                      dragActive
                        ? 'border-indigo-500 bg-indigo-50'
                        : validationError
                        ? 'border-red-300 bg-red-50'
                        : csvFile
                        ? 'border-green-300 bg-green-50'
                        : 'border-gray-300 hover:border-indigo-400'
                    }`}
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".csv"
                      onChange={(e) => e.target.files && handleFileSelect(e.target.files[0])}
                      className="hidden"
                    />

                    {!csvFile && (
                      <div className="text-center">
                        <Upload className="mx-auto h-12 w-12 text-gray-400" />
                        <div className="mt-4">
                          <button
                            onClick={() => fileInputRef.current?.click()}
                            className="text-indigo-600 hover:text-indigo-700 font-medium"
                          >
                            Click to upload
                          </button>
                          <span className="text-gray-600"> or drag and drop</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-2">CSV file up to 10MB</p>
                      </div>
                    )}

                    {csvFile && !validationError && (
                      <div className="text-center">
                        <CheckCircle className="mx-auto h-12 w-12 text-green-600" />
                        <div className="mt-4">
                          <p className="font-medium text-green-900">{csvFile.name}</p>
                          <p className="text-sm text-green-700 mt-1">
                            {(csvFile.size / 1024).toFixed(1)} KB
                          </p>
                        </div>
                        <button
                          onClick={() => {
                            setCsvFile(null);
                            setCsvPreview(null);
                          }}
                          className="mt-3 text-sm text-indigo-600 hover:text-indigo-700"
                        >
                          Choose different file
                        </button>
                      </div>
                    )}

                    {validationError && (
                      <div className="text-center">
                        <AlertCircle className="mx-auto h-12 w-12 text-red-600" />
                        <div className="mt-4">
                          <p className="font-medium text-red-900">Validation Error</p>
                          <p className="text-sm text-red-700 mt-1">{validationError}</p>
                        </div>
                        <button
                          onClick={() => {
                            setCsvFile(null);
                            setValidationError(null);
                          }}
                          className="mt-3 text-sm text-indigo-600 hover:text-indigo-700"
                        >
                          Try again
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Historical Data Detected Banner */}
                  {csvFile && detectedCsvType === 'HISTORICAL_ACTUALS' && !validationError && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                      <div className="flex items-start space-x-3">
                        <Database className="h-5 w-5 text-amber-600 mt-0.5" />
                        <div className="flex-1">
                          <h4 className="font-semibold text-amber-900 mb-1">
                            Historical Data Detected
                          </h4>
                          <p className="text-sm text-amber-700 mb-3">
                            This CSV contains daily historical volume data. It will be distributed into intervals and stored as actuals for accuracy tracking.
                          </p>

                          {/* Detected Channels */}
                          {detectedChannels.length > 0 && (
                            <div className="mb-3">
                              <span className="text-xs font-medium text-amber-800">Channels: </span>
                              <div className="inline-flex flex-wrap gap-1 mt-1">
                                {detectedChannels.map((channel) => (
                                  <span
                                    key={channel}
                                    className="px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-800 rounded-full"
                                  >
                                    {channel}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Interval Selection */}
                          <div className="flex items-center space-x-3">
                            <Clock className="h-4 w-4 text-amber-600" />
                            <label className="text-sm font-medium text-amber-800">
                              Distribute to:
                            </label>
                            <select
                              value={selectedIntervalMinutes}
                              onChange={(e) => setSelectedIntervalMinutes(parseInt(e.target.value))}
                              className="px-3 py-1 text-sm border border-amber-300 rounded-md bg-white focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                            >
                              <option value={15}>15-minute intervals</option>
                              <option value={30}>30-minute intervals</option>
                              <option value={60}>60-minute intervals</option>
                            </select>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* CSV Preview */}
                  {csvPreview && !validationError && (
                    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                      <div className="px-4 py-2 bg-gray-50 border-b border-gray-200">
                        <h4 className="text-sm font-medium text-gray-900">Preview (first 5 rows)</h4>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 text-xs">
                          <thead className="bg-gray-50">
                            <tr>
                              {csvPreview[0].map((header, idx) => (
                                <th
                                  key={idx}
                                  className="px-4 py-2 text-left font-medium text-gray-700 uppercase"
                                >
                                  {header}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {csvPreview.slice(1).map((row, rowIdx) => (
                              <tr key={rowIdx}>
                                {row.map((cell, cellIdx) => (
                                  <td key={cellIdx} className="px-4 py-2 text-gray-900">
                                    {cell}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}
                </>
              )}
            </div>

            {/* Footer - hidden during loading */}
            {!isLoading && (
              <div className="flex items-center justify-end space-x-3 px-6 py-4 bg-gray-50 border-t border-gray-200 rounded-b-xl">
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreate}
                  disabled={selectedMethod === 'CSV' && (!csvFile || !!validationError)}
                  className={`px-4 py-2 text-sm font-medium text-white rounded-md transition-colors ${
                    selectedMethod === 'CSV' && (!csvFile || !!validationError)
                      ? 'bg-gray-400 cursor-not-allowed'
                      : selectedMethod === 'HISTORICAL'
                      ? 'bg-purple-600 hover:bg-purple-700'
                      : 'bg-indigo-600 hover:bg-indigo-700'
                  }`}
                >
                  Create Forecast
                </button>
              </div>
            )}
          </motion.div>
        </div>

        {/* Model Comparison Modal */}
        <ModelComparisonModal
          isOpen={showModelComparison}
          onClose={() => setShowModelComparison(false)}
          onSelectModel={handleModelSelect}
          initialHistoricalWeeks={historicalWeeks}
          initialForecastWeeks={forecastWeeks}
        />
      </div>
    </AnimatePresence>
  );
}
